/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { Crown, Plus, Trash2, Loader2, ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAssignableTemplates, useAssignedTemplates, useAssignTemplate, useUnassignTemplate } from '@/hooks/api/useOrganizations';
import { useNotification } from '@/hooks/useNotification';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import AssignTemplateModal, { useAdminPreviewUrls } from './AssignTemplateModal';

interface PremiumTemplatesCardProps {
  orgId: string;
}

const PremiumTemplatesCard: React.FC<PremiumTemplatesCardProps> = ({ orgId }) => {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [unassignCode, setUnassignCode] = useState<string | null>(null);

  const { data: assignableTemplates = [], isLoading: loadingAssignable } = useAssignableTemplates();
  const { data: assignedTemplates = [], isLoading: loadingAssigned } = useAssignedTemplates(orgId);
  const assignMutation = useAssignTemplate();
  const unassignMutation = useUnassignTemplate();

  // Fetch preview images with auth for assigned templates
  const assignedCodes = useMemo(() => assignedTemplates.map((t) => t.code), [assignedTemplates]);
  const previewUrls = useAdminPreviewUrls(assignedCodes);

  const handleAssign = (code: string) => {
    assignMutation.mutate(
      { orgId, code },
      {
        onSuccess: () => {
          success(t('organizationDetail.premiumTemplates.assignSuccess'));
          setAssignModalOpen(false);
        },
        onError: () => {
          showError(t('organizationDetail.premiumTemplates.assignError'));
        },
      }
    );
  };

  const handleUnassignConfirm = () => {
    if (!unassignCode) return;
    unassignMutation.mutate(
      { orgId, code: unassignCode },
      {
        onSuccess: () => {
          success(t('organizationDetail.premiumTemplates.unassignSuccess'));
          setUnassignCode(null);
        },
        onError: () => {
          showError(t('organizationDetail.premiumTemplates.unassignError'));
          setUnassignCode(null);
        },
      }
    );
  };

  const templateBeingRemoved = assignedTemplates.find((t) => t.code === unassignCode);

  return (
    <>
      <div className="bg-surface border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-500" />
            {t('organizationDetail.premiumTemplates.title')}
          </h3>
          <button
            onClick={() => setAssignModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500/15 text-amber-500 rounded-lg hover:bg-amber-500/25 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('organizationDetail.premiumTemplates.assign')}
          </button>
        </div>

        {loadingAssigned ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-secondary" />
          </div>
        ) : assignedTemplates.length === 0 ? (
          <p className="text-secondary text-sm text-center py-6">
            {t('organizationDetail.premiumTemplates.none')}
          </p>
        ) : (
          <div className="space-y-2">
            {assignedTemplates.map((template) => (
              <div
                key={template.code}
                className="flex items-center justify-between py-2.5 px-3 bg-background rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-12 rounded border border-border overflow-hidden bg-surface flex items-center justify-center flex-shrink-0">
                    {previewUrls[template.code] ? (
                      <img
                        src={previewUrls[template.code]}
                        alt={template.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-secondary/40" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{template.name}</span>
                      <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-500 rounded-full">
                        Premium
                      </span>
                    </div>
                    <div className="text-xs text-secondary line-clamp-1">{template.description}</div>
                  </div>
                </div>
                <button
                  onClick={() => setUnassignCode(template.code)}
                  className="p-1.5 rounded-lg text-secondary hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title={t('organizationDetail.premiumTemplates.unassign')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assign Modal */}
      <AssignTemplateModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onAssign={handleAssign}
        isAssigning={assignMutation.isPending}
        assignableTemplates={assignableTemplates}
        assignedTemplates={assignedTemplates}
      />

      {/* Unassign Confirmation */}
      <ConfirmationDialog
        isOpen={!!unassignCode}
        variant="danger"
        title={t('organizationDetail.premiumTemplates.unassignConfirm.title')}
        message={t('organizationDetail.premiumTemplates.unassignConfirm.message', {
          name: templateBeingRemoved?.name || '',
        })}
        confirmText={t('organizationDetail.premiumTemplates.unassign')}
        isLoading={unassignMutation.isPending}
        onConfirm={handleUnassignConfirm}
        onCancel={() => setUnassignCode(null)}
      />
    </>
  );
};

export default PremiumTemplatesCard;
